import { getTheme } from '@fluentui/react';
import {
  ILegend,
  IVerticalStackedChartProps,
  IVSChartDataPoint,
  VerticalStackedBarChart,
} from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPeriodFilterData, PeriodFilterDataOption } from 'components/Filter/PeriodFilter';
import { getPeriodURLParams } from 'components/Filter/PeriodFilterHelper';
import AuditActionPicker, { AuditActionPickerGroupCodes } from 'components/Pickers/AuditActionPicker';
import TenantPicker, { PickerTenant, TenantPickerMode } from 'components/Pickers/TenantPicker';
import { getPeriodText } from 'globalFunctions';
import { globalStackTokensGapMedium } from 'globalStyles';
import { Widget67ResultDTO, WidgetTenantRiskChangesPeriods } from 'models/dto/widgets/widget67DTO';
import { EntityTypes } from 'models/entity';
import { isPartnerWidgetType, WidgetSizes, WidgetTenantConfig, WidgetWidth } from 'models/widget';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAuditActions } from 'services/Api/auditService';
import { apiGetWidgetDataTenantRiskChanges } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { enumCount } from 'utils/object';
import { getRiskChangesPeriodGradient } from 'utils/risk';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';
import {
  getConfiguredTenantIds,
  getTenantWidgetTenantById,
  getTenantWidgetTenantId,
  getTenantWidgetTenantName,
  sortTenantWidgetTenantsByName,
  TenantWidgetTenant,
  WithTenantId,
  withTenantId,
} from './tenantWidgetData';
import { useTenantDrillDown } from './useTenantDrillDown';

export class WidgetTenantRiskChangesConfig extends WidgetTenantConfig {
  actions: number[];

  constructor() {
    super();

    this.actions = [];
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.loadTenant(newRawConfig);
        if (newRawConfig.actions) {
          this.actions = [...newRawConfig.actions];
        }
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTenantRiskChangesConfig {
    const newConfig = this.cloneTenant() as WidgetTenantRiskChangesConfig;
    newConfig.actions = [...this.actions];

    return newConfig;
  }
}

interface IWidgetTenantRiskChangesProps extends IWidgetRendererProps {}

const WidgetTenantRiskChanges = (props: IWidgetTenantRiskChangesProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<Widget67ResultDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [colorGradient] = useState<string[]>(getRiskChangesPeriodGradient());
  const [tenants, setTenants] = useState<TenantWidgetTenant[]>([]);
  const [tenantsToShow, setTenantsToShow] = useState<TenantWidgetTenant[]>([]);
  const theme = getTheme();

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();

        const tenants: TenantWidgetTenant[] = isPartnerWidgetType(props.widget.widgetType)
          ? props.sharedData?.partnerCustomers ?? []
          : props.sharedData?.userOrgUnits ?? [];
        setTenants(tenants);
        const tenantIds = getConfiguredTenantIds(config.tenants, tenants);
        if (tenantIds.length === 0) {
          setData([]);
          setTenantsToShow([]);

          return;
        }

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        let actionIds = config.actions;
        if (actionIds.length === 0) {
          const actions = await apiGetAuditActions(accessToken);
          actionIds = actions
            .filter((action) => action.code.startsWith(AuditActionPickerGroupCodes[EntityTypes.Risk]))
            .map((action) => action.auditTrailActionId);
        }

        if (actionIds.length === 0) {
          setData([]);
          setTenantsToShow([]);

          return;
        }

        const data = await apiGetWidgetDataTenantRiskChanges(tenantIds, actionIds, accessToken, props.abortSignal);

        setData(data);

        const tenantsToShow = tenants.filter((tenant) =>
          data.some((s) => s.tenantId === getTenantWidgetTenantId(tenant)),
        );
        sortTenantWidgetTenantsByName(tenantsToShow);
        setTenantsToShow(tenantsToShow);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData?.partnerCustomers, props.sharedData?.userOrgUnits]);

  const loadConfig = (): WidgetTenantRiskChangesConfig => {
    const newConfig = new WidgetTenantRiskChangesConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  const getRiskChangesPeriodColor = (idx: number, isPlaceholder: boolean): string => {
    return isPlaceholder
      ? appContext.useDarkMode
        ? theme.palette.neutralSecondaryAlt
        : theme.palette.neutralLight
      : colorGradient[idx];
  };

  const getData = (): IVerticalStackedChartProps[] => {
    if (tenantsToShow.length === 0 || data.length === 0) return [];

    const graphData: WithTenantId<IVerticalStackedChartProps>[] = tenantsToShow.map((tenant) => {
      const tenantId = getTenantWidgetTenantId(tenant);
      const tenantData = data.find((s) => s.tenantId === tenantId);
      const tenantName = getTenantWidgetTenantName(tenant);
      const dataPoints: IVSChartDataPoint[] = [];

      for (let idx = enumCount(WidgetTenantRiskChangesPeriods) - 1; idx >= 0; idx--) {
        const key = WidgetTenantRiskChangesPeriods[idx];
        const riskIds = tenantData?.data[key as keyof typeof tenantData.data];
        if (!riskIds) continue;

        dataPoints.push({
          legend: periodText(idx),
          data: 1,
          yAxisCalloutData: riskIds.length.toString(),
          xAxisCalloutData: tenantName,
          color: getRiskChangesPeriodColor(idx, riskIds.length === 0),
        });
      }

      return {
        chartData: withTenantId(dataPoints, tenantId),
        xAxisPoint: tenantName,
        tenantId,
      };
    });

    return graphData;
  };

  const periodText = (idx: number): string => {
    return getPeriodText(WidgetTenantRiskChangesPeriods[idx], t);
  };

  const getLegends = (): ILegend[] => {
    const legends: ILegend[] = [];

    for (let idx = enumCount(WidgetTenantRiskChangesPeriods) - 1; idx >= 0; idx--) {
      legends.push({
        title: periodText(idx),
        color: getRiskChangesPeriodColor(idx, false),
      });
    }

    return legends;
  };

  const getFilterForColor = (color: string | undefined): string => {
    if (!color) return '';
    const idx = colorGradient.findIndex((c) => c === color);
    if (idx < 0) return '';

    let selectedOption: PeriodFilterDataOption;
    let selectedCustomStart = '';
    let selectedCustomEnd = '';

    switch (idx) {
      case 5: // last 3 years
        selectedOption = PeriodFilterDataOption.Custom;
        selectedCustomStart = new Date(new Date().getFullYear() - 3, new Date().getMonth(), new Date().getDate())
          .toISOString()
          .split('T')[0];
        selectedCustomEnd = new Date().toISOString().split('T')[0];
        break;
      case 4:
        selectedOption = PeriodFilterDataOption.LastYear;
        break;
      case 3:
        selectedOption = PeriodFilterDataOption.LastQuarter;
        break;
      case 2:
        selectedOption = PeriodFilterDataOption.LastMonth;
        break;
      case 1:
        selectedOption = PeriodFilterDataOption.LastWeek;
        break;
      case 0: // last 2 days
        selectedOption = PeriodFilterDataOption.Custom;
        selectedCustomStart = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 2)
          .toISOString()
          .split('T')[0];
        selectedCustomEnd = new Date().toISOString().split('T')[0];
        break;
      default:
        selectedOption = PeriodFilterDataOption.All;
        break;
    }

    const periodObj: IPeriodFilterData = {
      option: selectedOption,
      auditYear: 0,
      customStart: selectedCustomStart,
      customEnd: selectedCustomEnd,
    };

    return getPeriodURLParams(periodObj, 'latestChange');
  };

  const drillDown = useTenantDrillDown();

  const onClick = (data: IVSChartDataPoint) => {
    const tenant = getTenantWidgetTenantById(tenants, (data as WithTenantId<IVSChartDataPoint>).tenantId);
    if (!tenant) return;
    const filter = getFilterForColor(data.color);
    let url = '/risks/overview?clearFilter';
    if (filter) {
      url += `&${filter}`;
    }
    drillDown(tenant, url, isPartnerWidgetType(props.widget.widgetType));
  };

  //
  // Main render
  //
  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
      </IPStack>
    );
  }

  const shouldRotateXAxisLabels = data.length > 10 && props.widget.widgetSize !== WidgetSizes.Large;

  return (
    <IPStack verticalFill>
      <IPStack.Item grow>
        <VerticalStackedBarChart
          data={getData()}
          height={250}
          width={WidgetWidth[props.widget.widgetSize] - 80}
          wrapXAxisLables={!shouldRotateXAxisLabels}
          rotateXAxisLables={shouldRotateXAxisLabels}
          barWidth={'auto'}
          legendProps={{
            legends: getLegends(),
            centerLegends: true,
          }}
          onBarClick={(ev, data) => onClick(data as IVSChartDataPoint)}
          hideLabels={true}
          yAxisTickCount={6}
          yAxisTickFormat={(idx: number) => {
            const topIdx = enumCount(WidgetTenantRiskChangesPeriods) - idx;

            return periodText(topIdx);
          }}
          margins={{
            bottom: 35,
            top: 20,
            left: 100,
            right: 0,
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetTenantRiskChanges;

//
// Config
//
interface IWidgetConfigWidgetTenantRiskChangesProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTenantRiskChanges = (props: IWidgetConfigWidgetTenantRiskChangesProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'widgets']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [selectedAuditActions, setSelectedAuditActions] = useState<number[]>([]);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTenantRiskChangesConfig => {
    let newRawConfig = new WidgetTenantRiskChangesConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const updateTenants = (tenants: PickerTenant[]) => {
    setSelectedTenants(tenants.map((t) => t.tenantId));
    const config = getConfig(
      tenants.map((t) => t.tenantId),
      selectedAuditActions,
    );

    props.onUpdateConfig(JSON.stringify(config), selectedAuditActions.length > 0);
  };

  const updateActions = (actions: number[]) => {
    setSelectedAuditActions(actions);
    const config = getConfig(selectedTenants, actions);

    props.onUpdateConfig(JSON.stringify(config), actions.length > 0);
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const config = loadConfig();
      setSelectedTenants(config.tenants);
      setSelectedAuditActions(config.actions);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (tenants: string[], actions: number[]): WidgetTenantRiskChangesConfig => {
    const config = new WidgetTenantRiskChangesConfig();
    config.tenants = tenants;
    config.actions = actions;

    return config;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <TenantPicker
          mode={isPartnerWidgetType(props.widget.widgetType) ? TenantPickerMode.Partner : TenantPickerMode.User}
          onSelect={updateTenants}
          selectedTenants={selectedTenants}
          emptySelectionMeansAll
        />
      </IPStack.Item>
      <IPStack.Item>
        <AuditActionPicker
          label={t('widgets:TenantRiskChangesOrgUnit.Actions.Label')}
          groups={[EntityTypes.Risk]}
          onSelect={updateActions}
          selectedAuditActions={selectedAuditActions}
        />
      </IPStack.Item>
    </IPStack>
  );
};
